// src/App.js
import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';

import HomePage from './pages/HomePage';
import LandingPage from './components/LandingPage';
import ReviewPage from './pages/ReviewPage';
import SurveyDetailPage from './pages/SurveyDetailPage';
import AboutPage from './pages/AboutPage';
import UserGuidePage from './pages/UserGuidePage';
import DisclaimerPage from './pages/DisclaimerPage';
import AnonymousIdPolicyPage from './pages/AnonymousIdPolicyPage';
import FeedbackPage from './pages/FeedbackPage';
import InviteGatePage from './pages/InviteGatePage';
import WaitListSignUpPage from './pages/WaitListSignUpPage';
import PrivacyPage from './pages/PrivacyPage';
import SignInPage from './pages/SignInPage';
import SignUpPage from './pages/SignUpPage';
import ResetPasswordPage from './pages/ResetPasswordPage';

import CreateSurveyPage from './components/CreateSurveyPage';
import GeoConsentModal from './components/GeoConsentModal';

import { bootstrapBffSession } from './lib/apiClient';
import { GeoProvider, useGeo } from './context/GeoContext';
import './styles/app.css';

const AdminPage = React.lazy(() => import('./pages/AdminPage'));

function AppRoutes() {
  const { pathname } = useLocation();
  if (pathname === '/admin' || pathname === '/admin/') {
    return <React.Suspense fallback={<div style={{ padding: 24 }}>Loading…</div>}><AdminPage /></React.Suspense>;
  }
  return <AppShell />;
}

function AppShell() {
  const { geoEnabled, bootstrapped, status, promptAllowed } = useGeo();

  const [sessionBooted, setSessionBooted] = useState(false);
  const [invite, setInvite] = useState({ checked: false, required: false, ok: true });
  const [dismissed, setDismissed] = useState(() => {
    return sessionStorage.getItem('geo_modal_dismissed') === '1';
  });

  // Ensure BFF anon session is established before first API calls
  useEffect(() => {
    (async () => {
      try {
        await bootstrapBffSession();
        // Check invite gate status
        try {
          const s = await (await fetch('/api/activation/status', { credentials: 'include' })).json();
          setInvite({ checked: true, required: !!s.required, ok: !!s.ok });
        } catch (_) {
          setInvite({ checked: true, required: false, ok: true });
        }
      } finally {
        setSessionBooted(true);
      }
    })();
  }, []);

  // Re-check invite status on route changes to reflect newly entered codes
  const location = useLocation();
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await (await fetch('/api/activation/status', { credentials: 'include' })).json();
        if (!cancelled) setInvite({ checked: true, required: !!s.required, ok: !!s.ok });
      } catch (_) {
        if (!cancelled) setInvite((prev) => ({ ...prev, checked: true }));
      }
    })();
    return () => { cancelled = true; };
  }, [location.pathname]);

  // Keep this hook ABOVE any conditional return
  useEffect(() => {
    if (geoEnabled) {
      setDismissed(true);
      sessionStorage.setItem('geo_modal_dismissed', '1');
    }
  }, [geoEnabled]);

  // Early loading UI AFTER all hooks are declared
  if (!sessionBooted) {
    return <div style={{ padding: 16 }}>Loading…</div>;
  }

  const handleClose = () => {
    setDismissed(true);
    sessionStorage.setItem('geo_modal_dismissed', '1');
  };

  // Show modal after permission check, when user hasn’t dismissed
  const shouldShowModal =
    promptAllowed && bootstrapped && !geoEnabled && !dismissed && status !== 'requesting';

  return (
    <>
      {shouldShowModal && <GeoConsentModal open onClose={handleClose} />}
      <Routes>
          {/* Invite gate */}
          <Route path="/invite" element={<InviteGatePage />} />
          {/* If required and not ok, send all to /invite */}
          {invite.checked && invite.required && !invite.ok ? (
            <>
              <Route path="*" element={<Navigate to="/invite" replace />} />
            </>
          ) : (
            <>
          {/* Public core routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/guide" element={<UserGuidePage />} />
          <Route path="/disclaimer" element={<DisclaimerPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/mission/anonymous-surveys" element={<Navigate to="/about" replace />} />
          <Route path="/privacy/anonymous-id" element={<AnonymousIdPolicyPage />} />
          <Route path="/review" element={<ReviewPage />} />
          <Route path="/create-survey" element={<CreateSurveyPage />} />
          <Route path="/surveys/:id" element={<SurveyDetailPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/waitlist" element={<WaitListSignUpPage />} />
          <Route path="/feedback" element={<FeedbackPage />} />
          <Route path="/signin" element={<SignInPage />} />
          <Route path="/signup" element={<SignUpPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* Business flows removed */}

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
            </>
          )}
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <GeoProvider>
      <Router>
        <AppRoutes />
      </Router>
    </GeoProvider>
  );
}
